import * as React from 'react';
import { styled } from 'styled-components';
import { Skeleton } from '@mui/material';
import { Sections } from '../../../core/enums';
import BbocScoreCard from '../common/Cards/BbocScoreCard/BbocScoreCard';
import ProgressSection from '../common/Cards/FlightPlanCard/ProgressSectionComponent';
import { cardInnerGap, slateGrey, superLightGreyColour, tabletPortraitMinWidth } from '../../cssConstants';
import FormButtons from '../common/FormButton';
import { getQuestionSectionColour, Question } from './questions/questionData';
import { calculateOverallScore, calculateScore } from '../../../core/domain';
import SurveyAnswer from '../../../models/SurveyAnswer';
import { hasValue } from '../../../lib/lib';
import { NewCardWrapper } from '../common/CardLayout';
import { PageContent } from '../common/Layout';

const BbocScoreSubPage = ({
	surveyAnswers,
	surveyQuestions,
	buttonText = '',
	onButtonClick,
	hasIncompleteSurvey
}: Props) => {
	if (surveyAnswers === undefined) {
		return (
			<PageContent>
				<BbocScoreCard overallScore={undefined} showButton={false} showTooltip />
				<NewCardWrapper $innerGap="small">
					<Skeleton variant="text" width="40%" height={22} />
					<Skeleton variant="text" width="80%" height={16} />
					{[0, 1, 2, 3].map((i) => (
						<SkeletonSectionRow key={i}>
							<Skeleton variant="text" width="30%" height={16} />
							<Skeleton variant="rectangular" width="100%" height={8} sx={{ borderRadius: '4px' }} />
						</SkeletonSectionRow>
					))}
				</NewCardWrapper>
				<NewCardWrapper $innerGap="small">
					<Skeleton variant="text" width="50%" height={22} />
					<Skeleton variant="text" width="85%" height={16} />
					<Skeleton variant="text" width="70%" height={16} />
					<Skeleton
						variant="rectangular"
						width={200}
						height={44}
						sx={{ borderRadius: '50px', margin: 'auto', marginTop: '8px' }}
					/>
				</NewCardWrapper>
			</PageContent>
		);
	}

	return (
		<PageContent>
			<BbocScoreCard overallScore={calculateOverallScore(surveyAnswers)} showButton={false} showTooltip />
			<NewCardWrapper>
				<BbocTitle>Score Breakdown</BbocTitle>
				<BbocSubTitle>
					Here is how your Financial Progress Score is broken down over personal, business, wealth & estate.
				</BbocSubTitle>
				{Object.values(Sections)
					.map((section) => {
						const score = calculateSectionScore(section, surveyAnswers, surveyQuestions);
						return { section, score };
					})
					.filter(({ score }) => hasValue(score))
					.map(({ section, score }) => (
						<ProgressSection
							key={section}
							label={section}
							color={getQuestionSectionColour(section)}
							showCircle={false}
							currentStep={score}
							totalStep={100}
							height="8px"
						/>
					))}
			</NewCardWrapper>
			<NewCardWrapper>
				<>
					{hasIncompleteSurvey ? (
						<>
							<BbocTitle>Continue your progress</BbocTitle>
							<BbocSubTitle>You&apos;ve started but haven&apos;t finished. Continue to make progress.</BbocSubTitle>
						</>
					) : (
						<>
							<BbocTitle>Retake the challenge</BbocTitle>
							<BbocSubTitle>
								Update your score every six months for a clearer view of your financial wellness.
							</BbocSubTitle>
						</>
					)}
					{onButtonClick && <CustomButton continueButtonText={buttonText} onContinueButtonClick={onButtonClick} />}
				</>
			</NewCardWrapper>
		</PageContent>
	);
};

export default BbocScoreSubPage;

const calculateSectionScore = (section: Sections, surveyAnswers: SurveyAnswer[], questionsData: Question[]) => {
	const sectionQuestionAnswers = surveyAnswers?.filter(
		(answer) => answer.score !== null && isAnswerForSection(answer, section, questionsData)
	);
	if (!sectionQuestionAnswers || sectionQuestionAnswers.length === 0) return null;
	return calculateScore(sectionQuestionAnswers);
};

const isAnswerForSection = (answer: SurveyAnswer, sectionTitle: Sections, questionsData: Question[]) =>
	questionsData.find((x) => x.uniqueName === answer.questionUniqueName)?.section.title === sectionTitle;

interface Props {
	surveyAnswers: SurveyAnswer[];
	surveyQuestions: Question[];
	buttonText?: string;
	onButtonClick?: () => void;
	hasIncompleteSurvey: boolean;
}

const BbocTitle = styled.p`
	color: ${slateGrey};
	font-size: 14px;
	font-weight: 500;
	line-height: 24px;
	letter-spacing: 0.15px;
	margin-bottom: 12px;

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 18px;
	}
`;

const BbocSubTitle = styled.p`
	color: ${superLightGreyColour};
	font-size: 12px;
	font-weight: 400;
	line-height: 16.5px;
	letter-spacing: 0.25px;

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 14px;
	}
`;

const CustomButton = styled(FormButtons)`
	margin: 0;
`;

const SkeletonSectionRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${cardInnerGap};
`;
